import { useId, useState, type FormEvent } from 'react';

import useDashboardWidgetMutations from '@/features/dashboard/hooks/useDashboardWidgetMutations';
import type { WidgetId } from '@/features/dashboard/registry';
import { cn } from '@/lib/cn';
import { errorInputClasses, inputClasses } from '@/lib/formStyles';

import { DEFAULT_HSL_BUSES_CONFIG, mergeHslBusesConfig, type HslBusesConfig } from './config';

const STOP_ID_PATTERN = /^\d{1,10}$/;

interface FieldErrors {
  stopId?: string;
  walkMinutes?: string;
  leaveBufferMinutes?: string;
}

function validate(stopId: string, walkRaw: string, leaveRaw: string): { errors: FieldErrors; values: HslBusesConfig | null } {
  const errors: FieldErrors = {};

  if (!stopId) {
    errors.stopId = 'Required';
  } else if (!STOP_ID_PATTERN.test(stopId)) {
    errors.stopId = 'Numbers only (1–10 digits)';
  }

  let walkMinutes = NaN;
  if (walkRaw === '') {
    errors.walkMinutes = 'Required';
  } else {
    const n = Number(walkRaw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      errors.walkMinutes = 'Must be 0 or greater';
    } else {
      walkMinutes = n;
    }
  }

  let leaveBufferMinutes = NaN;
  if (leaveRaw === '') {
    errors.leaveBufferMinutes = 'Required';
  } else {
    const n = Number(leaveRaw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      errors.leaveBufferMinutes = 'Must be 0 or greater';
    } else {
      leaveBufferMinutes = n;
    }
  }

  const valid = Object.keys(errors).length === 0;
  return {
    errors,
    values: valid ? { stopId, walkMinutes, leaveBufferMinutes } : null,
  };
}

export default function HslBusesSettingsForm({
  widgetId,
  config,
  onSaved,
  formId,
}: {
  widgetId: WidgetId;
  config: Record<string, unknown>;
  onSaved: () => void;
  formId: string;
}) {
  const mutations = useDashboardWidgetMutations();
  const initial = mergeHslBusesConfig(config as Partial<HslBusesConfig>);

  const [stopId, setStopId] = useState(initial.stopId || DEFAULT_HSL_BUSES_CONFIG.stopId);
  const [walkRaw, setWalkRaw] = useState(String(initial.walkMinutes));
  const [leaveRaw, setLeaveRaw] = useState(String(initial.leaveBufferMinutes));

  const stopIdId = useId();
  const walkId = useId();
  const leaveId = useId();
  const stopIdErrId = useId();
  const walkErrId = useId();
  const leaveErrId = useId();

  const { errors, values } = validate(stopId, walkRaw, leaveRaw);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!values || mutations.isPending) return;
    mutations.updateConfig(widgetId, values as unknown as Record<string, unknown>);
    onSaved();
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={stopIdId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
          Stop ID
        </label>
        <input
          id={stopIdId}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          required
          value={stopId}
          onChange={(e) => setStopId(e.target.value.trim())}
          aria-invalid={errors.stopId ? true : undefined}
          aria-describedby={errors.stopId ? stopIdErrId : undefined}
          className={cn(inputClasses, errors.stopId && errorInputClasses)}
        />
        {errors.stopId && (
          <p id={stopIdErrId} className="text-xs text-(--accent-danger)">
            {errors.stopId}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={walkId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
          Walking time (min)
        </label>
        <input
          id={walkId}
          type="number"
          min={0}
          step={1}
          required
          value={walkRaw}
          onChange={(e) => setWalkRaw(e.target.value)}
          aria-invalid={errors.walkMinutes ? true : undefined}
          aria-describedby={errors.walkMinutes ? walkErrId : undefined}
          className={cn(inputClasses, errors.walkMinutes && errorInputClasses)}
        />
        {errors.walkMinutes && (
          <p id={walkErrId} className="text-xs text-(--accent-danger)">
            {errors.walkMinutes}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={leaveId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
          Leave buffer (min)
        </label>
        <input
          id={leaveId}
          type="number"
          min={0}
          step={1}
          required
          value={leaveRaw}
          onChange={(e) => setLeaveRaw(e.target.value)}
          aria-invalid={errors.leaveBufferMinutes ? true : undefined}
          aria-describedby={errors.leaveBufferMinutes ? leaveErrId : undefined}
          className={cn(inputClasses, errors.leaveBufferMinutes && errorInputClasses)}
        />
        {errors.leaveBufferMinutes && (
          <p id={leaveErrId} className="text-xs text-(--accent-danger)">
            {errors.leaveBufferMinutes}
          </p>
        )}
      </div>
    </form>
  );
}
